import React from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { formatDateTime, currencyRounded, getStatusBadgeStyle, maskMobile, capitalizeWords } from '../../utils/subscriberHelpers';
import ReadOnlyMedicineCartItem from '../medicine/ReadOnlyMedicineCartItem';
import ReadOnlyCartSummary from '../medicine/ReadOnlyCartSummary';
import OrderNoteCard from '../medicine/OrderNoteCard';

const TransactionDetailModal = ({
  mounted,
  show,
  transactionDetail,
  loading,
  error,
  partnerDataExpanded,
  setPartnerDataExpanded,
  currentRole,
  mobilesUnmasked,
  onClose,
  onRetry
}) => {
  if (!mounted || !show) return null;

  return createPortal(
    <div 
      className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-50" 
      onClick={onClose}
      style={{ margin: 0, padding: '1rem' }}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200 bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-900">Transaction Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-600 mb-2">{error}</div>
              <button
                onClick={onRetry}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          ) : transactionDetail ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Date & Time</div>
                  <div className="text-sm font-medium text-gray-900">
                    {(() => {
                      let dateTimeValue = transactionDetail.date_time;
                      if (typeof dateTimeValue === 'object' && dateTimeValue !== null && !(dateTimeValue instanceof Date)) {
                        if (dateTimeValue.start_date && dateTimeValue.end_date) {
                          let start = dateTimeValue.start_date;
                          let end = dateTimeValue.end_date;
                          if (typeof start === 'object' && start !== null && !(start instanceof Date)) {
                            start = formatDateTime(start);
                          } else {
                            start = formatDateTime(String(start));
                          }
                          if (typeof end === 'object' && end !== null && !(end instanceof Date)) {
                            end = formatDateTime(end);
                          } else {
                            end = formatDateTime(String(end));
                          }
                          dateTimeValue = `${start} to ${end}`;
                        } else {
                          dateTimeValue = 'N/A';
                        }
                      } else {
                        return formatDateTime(dateTimeValue || 'N/A');
                      }
                      return dateTimeValue;
                    })()}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Subscriber Name</div>
                  <div className="text-sm font-medium text-gray-900">{transactionDetail.subscriber_name || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Mobile Number</div>
                  <div className="text-sm font-medium text-gray-900">
                    {(currentRole === 'Support' || currentRole === 'Finance' || !mobilesUnmasked)
                      ? maskMobile(transactionDetail.mobile_number)
                      : (transactionDetail.mobile_number || 'N/A')}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">REF CODE</div>
                  <div className="text-sm font-medium text-gray-900">{transactionDetail.ref_code || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Service Provider</div>
                  <div className="text-sm font-medium text-gray-900">{transactionDetail.service_provider || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Description</div>
                  <div className="text-sm font-medium text-gray-900">{capitalizeWords(transactionDetail.description || 'N/A')}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Price</div>
                  <div className="text-sm font-medium text-gray-900">
                    ₹{(transactionDetail.price || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Cashback</div>
                  <div className="text-sm font-medium text-gray-900">
                    {currencyRounded(transactionDetail.cashback || 0)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">EC (Eligible Cashback)</div>
                  <div className="text-sm font-medium text-gray-900">
                    {currencyRounded(transactionDetail.eligible_cashback || 0)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">RC (Redeemable Cashback)</div>
                  <div className="text-sm font-medium text-gray-900">
                    {currencyRounded(transactionDetail.redeemable_cashback || 0)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">WB (Wallet Balance)</div>
                  <div className="text-sm font-medium text-gray-900">
                    {currencyRounded(transactionDetail.wallet_balance || 0)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Family Members</div>
                  <div className="text-sm font-medium text-gray-900" title={transactionDetail.family_member_names || ''}>
                    {transactionDetail.family_member_names || 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Status</div>
                  <span 
                    className="inline-flex px-2 py-1 text-xs font-semibold rounded-full border justify-center"
                    style={getStatusBadgeStyle(transactionDetail.status)}
                  >
                    {transactionDetail.status}
                  </span>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Payment Method</div>
                  <div className="text-sm font-medium text-gray-900">{transactionDetail.payment_method || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">AP Transaction ID</div>
                  <div className="text-sm font-medium text-gray-900 font-mono">{transactionDetail.ap_transaction_id || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">3rd Party Booking Ref</div>
                  <div className="text-sm font-medium text-gray-900">{transactionDetail.third_party_booking_ref_code || 'N/A'}</div>
                </div>
              </div>
              
              {Array.isArray(transactionDetail.partner_data) && transactionDetail.partner_data
                .filter(orderData => orderData?.order_note && String(orderData.order_note).trim())
                .map((orderData, orderIndex) => (
                  <div key={`note-${orderData.order_id || orderIndex}`} className="border-t border-gray-200 pt-4">
                    <OrderNoteCard note={String(orderData.order_note)} />
                  </div>
                ))}

              {transactionDetail && (
                <div className="border-t border-gray-200 pt-4">
                  <div 
                    className="text-sm font-semibold text-gray-700 cursor-pointer hover:text-gray-900 flex items-center"
                    onClick={() => setPartnerDataExpanded(!partnerDataExpanded)}
                  >
                    <span className="mr-2">Partner Data</span>
                    <span className="text-xs text-gray-500 font-normal">(Click to expand)</span>
                    {partnerDataExpanded ? (
                      <svg className="w-4 h-4 ml-2 text-gray-500 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 ml-2 text-gray-500 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>
                  {partnerDataExpanded && (
                    <div className="mt-3">
                      {transactionDetail.partner_data !== undefined && transactionDetail.partner_data !== null && Array.isArray(transactionDetail.partner_data) && transactionDetail.partner_data.length > 0 ? (
                        <div className="space-y-4">
                          {transactionDetail.partner_data.map((orderData, orderIndex) => (
                            <div key={orderData.order_id || orderIndex} className="border border-gray-200 rounded-md p-4 bg-white">
                              {/* Cart Items */}
                              {orderData.cart_items && Array.isArray(orderData.cart_items) && orderData.cart_items.length > 0 ? (
                                <div className="space-y-3 mb-4">
                                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Cart Items:</h4>
                                  {orderData.cart_items
                                    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                                    .map((item) => (
                                      <ReadOnlyMedicineCartItem key={item.id || item.service_item_id} item={item} />
                                    ))}
                                </div>
                              ) : (
                                <div className="mb-4 text-sm text-gray-500 text-center py-4">
                                  No cart items available
                                </div>
                              )}
                              
                              {/* Cart Summary */}
                              <ReadOnlyCartSummary orderData={orderData} finalPrice={transactionDetail?.price} />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-6 bg-gray-50 rounded-md border border-gray-200 text-sm text-gray-500 text-center">
                          No partner data available
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
        
        <div className="flex justify-end px-6 py-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TransactionDetailModal;

